import { createEffect, createMemo, createUniqueId, type JSX, on, Show } from 'solid-js';
import { t } from '../i18n';
import { type FleetLiveModel, fleetLiveModel } from '../lib/fleet-live';
import { createArrange, PANEL_NAME, PANEL_SHORT, ZONE_NAME } from '../lib/fleet-live-arrange';
import { coreModel } from '../lib/fleet-live-core';
import { createLiveLoop } from '../lib/fleet-live-loop';
import type { FleetLiveRead } from '../lib/fleet-live-read';
import { FLEET_LIVE_PANELS } from '../lib/fleet-live-vis';
import type { ToastTone } from '../lib/toast';
import { type DockSwitch, FleetLiveDock } from './FleetLiveDock';
import { FleetLiveDragGhost } from './FleetLiveDragGhost';
import { FleetLivePip } from './FleetLivePip';
import { FleetLiveZones } from './FleetLiveZones';
import { QueryBoundary } from './QueryBoundary';

// The Fleet Live section (#6778, #6779; epic #6552): the dock, the all-hidden line, the layout
// zones with Core Intelligence, the Command Deck and the Topology (`FleetLiveZones`), the
// floating Core and the drag ghost, on ONE frame loop. Presenter over `useFleetLive`'s read;
// what shows and where is `createArrange`'s.
//
// THE ONE WRITE is the Merge Gate's (`FleetLiveCore` → `FleetLiveGate`), offered on
// `canWrite` (the Fleet page's `roleSettled && canAdminWrite`). Everything else here, the
// switches, "Hide all", Sound, the grips and "Place in", is a viewer preference shown to
// every role.

export type FleetLiveSectionProps = {
  readonly read: FleetLiveRead;
  readonly token: string | null;
  readonly canWrite: boolean;
  /** Any dialog of the Fleet page open, the machine details panel included. */
  readonly dialogOpen: () => boolean;
  readonly notify: (message: string, tone?: ToastTone) => void;
};

export function FleetLiveSection(props: FleetLiveSectionProps): JSX.Element {
  const headingId = createUniqueId();
  const loop = createLiveLoop();
  const arrange = createArrange(loop);

  const model = createMemo((): FleetLiveModel | undefined => {
    const wire = props.read.wire();
    return wire === undefined ? undefined : fleetLiveModel(wire);
  });
  const core = createMemo(() => {
    const wire = props.read.wire();
    return wire === undefined ? undefined : coreModel(wire);
  });
  // One motion note per network chunk: a snapshot lands as many synchronous row writes,
  // and noting each would read the half-applied set as items leaving.
  let noting = false;
  createEffect(
    on(model, (m) => {
      if (m === undefined) return;
      loop.invalidate();
      if (loop.reducedMotion() || noting) return;
      noting = true;
      queueMicrotask(() => {
        noting = false;
        const now = model();
        if (now !== undefined) loop.motion.noteItems(now.items, performance.now());
      });
    }),
  );

  const switches: readonly DockSwitch[] = FLEET_LIVE_PANELS.map((key) => ({
    key,
    label: PANEL_SHORT[key](),
  }));
  // The boundary sees only WHETHER there is a body, so it mounts the panels once; they read
  // the model themselves and patch in place, rather than re-mounting (and resetting every
  // canvas) on each stream patch.
  const ready = createMemo(() => model() !== undefined);
  const boundary = {
    get isLoading() {
      return props.read.query.isLoading && model() === undefined;
    },
    get isError() {
      return props.read.query.isError && model() === undefined;
    },
    get error() {
      return props.read.query.error;
    },
    get data() {
      return ready() ? true : undefined;
    },
    refetch: () => props.read.query.refetch(),
  };
  const dragged = (): string | null => {
    const k = arrange.drag.active();
    return k === null ? null : PANEL_NAME[k]();
  };
  const over = (): string | null => {
    const z = arrange.drag.over();
    return z === null ? null : ZONE_NAME[z]();
  };

  return (
    <section class="fleet-live" aria-labelledby={headingId} ref={(el) => loop.observe(el)}>
      <FleetLiveDock
        headingId={headingId}
        streaming={model()?.streaming ?? null}
        switches={switches}
        vis={arrange.vis()}
        onToggle={(key) => arrange.setPanel(key, !arrange.vis()[key])}
        onAll={() => arrange.all()}
        sound={arrange.sound()}
        onSound={() => arrange.toggleSound()}
      />
      <Show when={!arrange.anyShown()}>
        <div class="fleet-live__rest">
          <span>{t('fleet.live.all_hidden')}</span>
          <button type="button" class="fleet-live__link" onClick={() => arrange.all()}>
            {t('fleet.live.show_all')}
          </button>
        </div>
      </Show>
      <QueryBoundary
        query={boundary}
        loading={<p class="fleet-live__loading">{t('fleet.live.loading')}</p>}
      >
        {() => (
          <Show when={model()}>
            {(m) => (
              <Show when={props.read.wire()}>
                {(wire) => (
                  <FleetLiveZones
                    arrange={arrange}
                    loop={loop}
                    model={m()}
                    wire={wire()}
                    token={props.token}
                    canWrite={props.canWrite}
                    notify={props.notify}
                  />
                )}
              </Show>
            )}
          </Show>
        )}
      </QueryBoundary>
      <Show when={core()}>
        {(c) => (
          <FleetLivePip
            model={c()}
            feed={model()?.events[0] ?? null}
            loop={loop}
            core={() => arrange.wrap('core')}
            coreShown={() => arrange.vis().core}
            dialogOpen={props.dialogOpen}
            cue={arrange.cue}
            onGoTo={() => arrange.goTo('core')}
          />
        )}
      </Show>
      <FleetLiveDragGhost panel={dragged()} zone={over()} at={arrange.drag.ghost()} />
      <div class="sr-only" aria-live="polite">
        {arrange.announce()}
      </div>
    </section>
  );
}
